import { PaginatedResource } from '../../typings/Paginated';
import { BaseService, ClientError, GenericPaginatedParameters, InvalidValue, PartialBy, Unauthorized, UnexpectedError } from './base-service';

function createProtectiveMeasureFormData(measure: ProtectiveMeasureDraft, guardianWomanId: number): FormData {
    const formData = new FormData();
    const localFields = ['aggressorImages', 'policeReportFile', 'aggressorPhotoFiles', 'aggressorPhotoIdsToDelete', 'removePoliceReport'];
    const data = Object.fromEntries(Object.entries(measure).filter(([key]) => !localFields.includes(key)));
    delete data.id;
    data.guardianWomanId = guardianWomanId;
    formData.append('protectiveMeasure', JSON.stringify(data));
    if (measure.policeReportFile) {
        formData.append('policeReport', measure.policeReportFile, measure.policeReportFile.name);
    }
    if (measure.removePoliceReport) {
        formData.append('removePoliceReport', 'true');
    }
    return formData;
}

export class GuardianAppService extends BaseService {
    private async getMutationError(res: Response): Promise<never> {
        if (res.status === 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code === 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        throw new UnexpectedError();
    }

    async getPaginatedGuardianWoman(params: GenericPaginatedParameters): Promise<PaginatedResource<GuardianWoman>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/guardian-woman${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getGuardianWomanById(id: number): Promise<GuardianWoman> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/guardian-woman/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async createGuardianWoman(params: Partial<Omit<GuardianWoman, 'id'>>): Promise<GuardianWoman> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/guardian-woman`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async editGuardianWoman(params: PartialBy<GuardianWoman, 'id'>): Promise<GuardianWoman> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/guardian-woman/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async createProtectiveMeasure(guardianWomanId: number, measure: ProtectiveMeasureDraft): Promise<ProtectiveMeasure> {
        const token = await this.getToken();
        const formData = createProtectiveMeasureFormData(measure, guardianWomanId);
        const res = await fetch(`${this.centralEndpoint}/guardian-woman/${guardianWomanId}/protective-measure`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: formData,
        });

        if (res.status !== 200 && res.status !== 201) {
            return this.getMutationError(res);
        }
        return res.json();
    }

    async editProtectiveMeasure(guardianWomanId: number, id: number, measure: ProtectiveMeasureDraft): Promise<ProtectiveMeasure> {
        const token = await this.getToken();
        const formData = createProtectiveMeasureFormData(measure, guardianWomanId);
        const res = await fetch(`${this.centralEndpoint}/guardian-woman/${guardianWomanId}/protective-measure/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token),
            body: formData,
        });

        if (res.status !== 200) {
            return this.getMutationError(res);
        }
        return res.json();
    }

    async deleteProtectiveMeasure(guardianWomanId: number, id: number): Promise<void> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/guardian-woman/${guardianWomanId}/protective-measure/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status !== 200 && res.status !== 204) {
            return this.getMutationError(res);
        }
    }

    async addProtectiveMeasurePhoto(guardianWomanId: number, protectiveMeasureId: number, photo: File): Promise<ProtectiveMeasurePhoto> {
        const token = await this.getToken();
        const formData = new FormData();
        formData.append('photo', photo, photo.name);

        const res = await fetch(`${this.centralEndpoint}/guardian-woman/${guardianWomanId}/protective-measure/${protectiveMeasureId}/photo`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: formData,
        });

        if (res.status !== 200 && res.status !== 201) {
            return this.getMutationError(res);
        }
        return res.json();
    }

    async deleteProtectiveMeasurePhoto(guardianWomanId: number, protectiveMeasureId: number, id: number): Promise<void> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/guardian-woman/${guardianWomanId}/protective-measure/${protectiveMeasureId}/photo/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status !== 200 && res.status !== 204) {
            return this.getMutationError(res);
        }
    }

    async deleteGuardianWoman(id: number): Promise<void> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/guardian-woman/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

}

export const guardianAppService = new GuardianAppService();
